import React, { useEffect, useMemo, useState } from 'react';
import './App.css';
import { getB24, BitrixProvider, useBitrix } from './context/BitrixContext';

type OwenDevice = {
  id: number | string;
  name?: string;
  identifier?: string;
  type?: string;
  time_zone?: string;
  is_favourite?: boolean;
  is_online?: boolean;
  has_alarm?: boolean;
  has_unread?: boolean;
  status?: string;
  categories?: Array<string | number>;
  [key: string]: unknown;
};

type OwenResponse = {
  mode?: string;
  field?: string;
  object?: Record<string, unknown> | null;
  available_objects?: Array<Record<string, unknown>>;
  devices?: OwenDevice[];
  devices_error?: string | null;
};

const OWEN_FIELD_FALLBACK = 'UF_CRM_1777219814';

const statusLabel = (device: OwenDevice) => {
  if (device.has_alarm) return 'alarm';
  if (device.is_online) return 'online';
  return device.status || 'offline';
};

const statusTone = (device: OwenDevice) => {
  const status = statusLabel(device);
  if (status === 'online') return 'success';
  if (status === 'alarm' || status === 'unreadalarm') return 'danger';
  return 'muted';
};

const AppBody = () => {
  const { loading, error, backendResponse, backendLoading, backendError, dealId } = useBitrix();
  const [editing, setEditing] = useState(false);
  const [savingDeviceId, setSavingDeviceId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const owen = (backendResponse?.owen ?? {}) as OwenResponse;
  const deal = (backendResponse?.deal ?? {}) as Record<string, unknown>;
  const fieldCode = String(owen.field || OWEN_FIELD_FALLBACK);
  const savedOwenId = String(deal[fieldCode] ?? '').trim();
  const devicesSource = owen.devices;
  const devices = useMemo(() => (Array.isArray(devicesSource) ? devicesSource : []), [devicesSource]);
  const savedObject = (owen.object ?? null) as Record<string, unknown> | null;

  const savedDevice = useMemo(() => {
    if (!savedOwenId) return null;
    return (
      devices.find((device) => String(device.id) === savedOwenId) ||
      devices.find((device) => String(device.identifier) === savedOwenId) ||
      null
    );
  }, [devices, savedOwenId]);
  const selectedNameSource = savedObject?.name ?? savedObject?.title ?? savedOwenId;
  const selectedName = savedDevice?.name || String(selectedNameSource || 'Selected device');
  const selectedType = savedDevice?.type || (savedObject?.type ? String(savedObject.type) : '');
  const selectedIdentifier = savedDevice?.identifier || (savedObject?.identifier ? String(savedObject.identifier) : '');
  const selectedStatus = savedDevice ? statusLabel(savedDevice) : String(savedObject?.status ?? 'saved');

  useEffect(() => {
    if (savedOwenId) {
      setEditing(false);
    }
  }, [savedOwenId]);

  const handleSelectDevice = async (device: OwenDevice) => {
    const b24 = getB24();
    if (!b24 || !b24.callMethod) {
      setActionError('Bitrix SDK is not initialized');
      return;
    }

    if (!dealId) {
      setActionError('Deal ID is missing');
      return;
    }

    setActionError(null);
    setSavingDeviceId(String(device.id));

    try {
      await b24.callMethod('crm.deal.update', {
        id: Number(dealId),
        fields: {
          [fieldCode]: String(device.id),
        },
      });

      window.location.reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save Owen ID';
      setActionError(message);
    } finally {
      setSavingDeviceId(null);
    }
  };

  if (loading) {
    return (
      <main className="App">
        <section className="status-card booting">
          <div className="status-eyebrow">Bitrix to FastAPI gate</div>
          <h1>Loading Bitrix session</h1>
          <p>Waiting for the Bitrix SDK and backend response.</p>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="App">
        <section className="status-card error">
          <div className="status-eyebrow">Bitrix to FastAPI gate</div>
          <h1>Access denied</h1>
          <p>{error}</p>
          <div className="status-footnote">
            Open this app only inside Bitrix24 and make sure the backend is running.
          </div>
        </section>
      </main>
    );
  }

  if (!backendResponse) {
    return (
      <main className="App">
        <section className="status-card error">
          <div className="status-eyebrow">Bitrix to FastAPI gate</div>
          <h1>Backend test failed</h1>
          <p>{backendError || 'No backend response received.'}</p>
        </section>
      </main>
    );
  }

  const showPicker = editing || !savedOwenId;
  const devicesError = owen.devices_error ? String(owen.devices_error) : null;

  return (
    <main className="App">
      <section className="status-card ok">
        <div className="status-eyebrow">Bitrix to FastAPI gate</div>
        <h1>{savedOwenId ? 'Saved Owen device' : 'Select Owen device'}</h1>
        <p>
          {backendLoading
            ? 'Loading backend data...'
            : savedOwenId
              ? 'The deal already has an Owen device connected.'
              : 'Choose a device to save it into the deal field.'}
        </p>

        {backendError ? <div className="status-footnote error-text">{backendError}</div> : null}
        {actionError ? <div className="status-footnote error-text">{actionError}</div> : null}

        {!showPicker && savedOwenId ? (
          <div className="summary-panel">
            <div className="summary-title">Current device</div>
            <div className="device-name">{selectedName}</div>
            <div className="device-meta">
              <span className={`pill pill-${savedDevice ? statusTone(savedDevice) : 'muted'}`}>
                {selectedStatus}
              </span>
              {selectedType ? <span className="pill">{selectedType}</span> : null}
              {selectedIdentifier ? <span className="pill">ID {selectedIdentifier}</span> : null}
            </div>
            <div className="device-details">
              <div><span>Bitrix field</span><b>{fieldCode}</b></div>
              <div><span>Saved Owen ID</span><b>{savedOwenId}</b></div>
              <div><span>Online</span><b>{savedDevice ? (savedDevice.is_online ? 'yes' : 'no') : 'unknown'}</b></div>
              <div><span>Alarm</span><b>{savedDevice ? (savedDevice.has_alarm ? 'yes' : 'no') : 'unknown'}</b></div>
            </div>
            <div className="actions">
              <button type="button" className="secondary-button" onClick={() => setEditing(true)}>
                Change Owen ID
              </button>
            </div>
          </div>
        ) : null}

        {showPicker ? (
          <div className="picker-panel">
            <div className="summary-title">
              {savedOwenId ? 'Choose a new device' : 'Available devices'}
            </div>
            {devicesError ? <div className="status-footnote error-text">{devicesError}</div> : null}
            {devices.length ? (
              <div className="device-grid">
                {devices.map((device) => {
                  const key = String(device.id);
                  const isSaving = savingDeviceId === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      className="device-button"
                      onClick={() => handleSelectDevice(device)}
                      disabled={Boolean(savingDeviceId)}
                    >
                      <div className="device-button-top">
                        <div className="device-button-name">{device.name || `Device ${key}`}</div>
                        <span className={`pill pill-${statusTone(device)}`}>{statusLabel(device)}</span>
                      </div>
                      <div className="device-button-meta">
                        {device.type ? <span>{device.type}</span> : null}
                        {device.identifier ? <span>Identifier {device.identifier}</span> : null}
                        {device.time_zone ? <span>{device.time_zone}</span> : null}
                      </div>
                      <div className="device-button-footer">
                        <span>Owen ID: {key}</span>
                        {isSaving ? <span>Saving...</span> : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">No devices returned by Owen Cloud.</div>
            )}
            {savedOwenId ? (
              <div className="actions">
                <button type="button" className="secondary-button" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>
    </main>
  );
};

export default function App() {
  return (
    <BitrixProvider>
      <AppBody />
    </BitrixProvider>
  );
}
